/**
 * Each envío's service date on a goods DeCA, and whether its envíos print more than one (#135).
 *
 * The document's own `fecha_realizacion` is envío 1's, as the server derives it. A grouping keeps
 * each envío's own date (BOE Sixth), and so does an amendment of one envío's date, so the envíos of
 * one DeCA may run on different days; the PDF prints each envío's date under its row. Dates are
 * compared as the PDF prints them, with `serviceDateValue`: the same instant with and without its
 * hour is two dates, and two instants of a day that has no hour are one.
 */

import type { Consignment, Deca } from '@deca/api'

import { serviceDateValue } from './format.ts'

/**
 * An envío's date and whether it has an hour. An envío that has none — a draft's — takes the
 * document's, and a missing flag the document's flag, as the contract reads both.
 */
export function consignmentServiceDate(row: Consignment, deca: Deca): { iso: string | null; hasTime: boolean } {
  if (!row.fecha_realizacion) return { iso: deca.fecha_realizacion ?? null, hasTime: deca.fechaRealizacionHasTime ?? true }
  return { iso: row.fecha_realizacion, hasTime: row.fechaRealizacionHasTime ?? deca.fechaRealizacionHasTime ?? true }
}

/** An envío's date as the two controls carry it, and as the PDF prints it. */
export function consignmentDateValue(row: Consignment, deca: Deca): string {
  const { iso, hasTime } = consignmentServiceDate(row, deca)
  return serviceDateValue(iso, hasTime)
}

/** Whether the envíos of this DeCA print more than one date. */
export function consignmentDatesDiffer(deca: Deca): boolean {
  return new Set(deca.consignments.map((row) => consignmentDateValue(row, deca))).size > 1
}
